import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { buildWorkflowPayload } from '../../containers/data-management/components/data-management.helpers';
import { createWorkflow, deleteWorkflow, getWorkflows, updateWorkflow } from './workflows-client';
import type { Workflow } from './workflows-client.type';

/**
 * Hook to create a workflow
 * @param {Function} onSuccess - The function to call when the mutation is successful
 * @param {string} organizationId - The organization ID
 * @returns {MutationResult} The mutation result
 */
function useCreateWorkflow({ organizationId, onSuccess }: { organizationId: string; onSuccess: () => void }) {
  const mutation = useMutation({
    mutationKey: ['createWorkflow'],
    mutationFn: ({ workflow }: { workflow: Workflow }) =>
      createWorkflow({ payload: buildWorkflowPayload(workflow), organizationId }),
    onSuccess,
  });

  return mutation;
}

/**
 * Hook to update a workflow
 *
 * @param {Function} onSuccess - The function to call when the mutation is successful
 * @param {string} organizationId - The organization ID
 * @returns {MutationResult} The mutation result
 */
function useUpdateWorkflow({ organizationId, onSuccess }: { organizationId: string; onSuccess: () => void }) {
  const mutation = useMutation({
    mutationKey: ['updateWorkflow'],
    mutationFn: ({ workflowId, workflow }: { workflowId: string | null; workflow: Workflow }) =>
      workflowId
        ? updateWorkflow({ workflowId, payload: buildWorkflowPayload(workflow), organizationId })
        : Promise.reject({ error: 'Workflow ID is required' }),
    onSuccess,
  });

  return mutation;
}

/**
 * Hook to get workflows
 * @param {string | null} organizationId The organization ID
 * @param {string | null | undefined} portfolioId The portfolio ID
 * @returns {QueryResult} The query result
 */
function useGetWorkflows(
  organizationId: string | null,
  portfolioId?: string | null,
): {
  data: Workflow[] | undefined;
  query: UseQueryResult<Workflow[]>;
} {
  const query = useQuery({
    queryKey: ['workflows', organizationId, portfolioId],
    queryFn: organizationId
      ? () => getWorkflows(organizationId, portfolioId ?? null)
      : () => Promise.reject({ error: 'Organization ID is required' }),
  });

  return { data: query.data, query };
}

/**
 * Hook to delete a workflow
 *
 * @param {Function} onSuccess - The function to call when the mutation is successful
 * @param {string} organizationId - The organization ID
 * @returns {MutationResult} The mutation result
 */
function useDeleteWorkflow({ organizationId, onSuccess }: { organizationId: string; onSuccess: () => void }) {
  const mutation = useMutation({
    mutationKey: ['deleteWorkflow'],
    mutationFn: ({ workflowId }: { workflowId: string }) => deleteWorkflow(workflowId, organizationId),
    onSuccess,
  });

  return mutation;
}

export { useCreateWorkflow, useDeleteWorkflow, useGetWorkflows, useUpdateWorkflow };
